/**
 * @fileoverview Host-agnostic polling contract for worker-owned geo conversion and geo parse background jobs.
 * @module import-editor/utils/geoConversionJob
 *
 * Responsibilities:
 * - Describe the async submission response every host receives when an API routes a geo file to the importProcessing worker.
 * - Describe the conversion-job polling snapshot the hosts read back from `backgroundJobs`.
 * - Own the single bounded polling loop (interval, timeout, stale detection, terminal handling) shared by tenant-web and system-web.
 *
 * Dependencies:
 * - None — the HTTP call, telemetry sink, and endpoint path are injected by the host service.
 *
 * Architecture:
 * - Pure workflow helper. The package never imports a host apiClient, Dexie handle, or telemetry singleton;
 *   hosts inject `fetchJob` and an optional `onEvent` sink so system-web and tenant-web cannot drift on
 *   timeout, stale, cancellation, or missing-result semantics.
 * - Geo upload and geo parse share one loop because both are the same `backgroundJobs` lifecycle with a
 *   different terminal payload shape, expressed through the `TResult` generic.
 *
 * Notes:
 * - Polling is an approved online-only path: the caller must await a converted blob or parse sample before
 *   the wizard can continue. No Dexie read or write happens here.
 * - Error messages are user-facing and retryable by design; worker errors are already sanitized server-side.
 */

/**
 * @contract GeoConversionJobStatus
 * Purpose: Closed enum of worker-owned geo conversion/parse lifecycle states.
 * Fields: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled'.
 * Rules: Terminal states are 'completed', 'failed', and 'cancelled'; only 'completed' carries a result.
 * Sync: Mirrors the server-authoritative `backgroundJobs.status` field.
 */
export type GeoConversionJobStatus = 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';

/**
 * @contract GeoConversionJobResponse
 * Purpose: Polling snapshot for a worker-owned geo upload conversion or geo parse job.
 * Fields: jobId, jobType, status, progress, message, result, error.
 * Rules: result is populated only for completed jobs; error is populated for terminal failures.
 * Sync: Read from the server-authoritative `backgroundJobs` document through explicit online polling.
 */
export interface GeoConversionJobResponse<TResult = Record<string, unknown>> {
  jobId: string;
  jobType: 'geoConversion' | 'geoParse';
  status: GeoConversionJobStatus;
  progress: { percent?: number; phase?: string } | null;
  message: string | null;
  result: TResult | null;
  error: string | null;
}

/**
 * @contract AsyncGeoSubmission
 * Purpose: Initial API response indicating that geo work moved to the importProcessing worker.
 * Fields: jobId, converting/parsing status, and the upload runId when the surface reserved one.
 * Rules: Callers must poll jobId before using any upload or parse metadata.
 * Sync: Transient HTTP response; durable state lives in `backgroundJobs`.
 */
export interface AsyncGeoSubmission {
  jobId: string;
  status: 'converting' | 'parsing';
  runId?: string;
}

/**
 * @contract GeoUploadConversionResult
 * Purpose: Terminal payload of a `geoConversion` job — the converted GeoParquet the wizard imports from.
 * Fields: blobUrl, filename, fileType, sourceFormat?, sizeBytes.
 * Rules: The reserved upload runId comes from the submitting response, not this payload;
 *   `sizeBytes` is the converted output size and maps to each host's UploadResult.fileSize.
 * Sync: Stored in the completed `backgroundJobs` payload by the importProcessing worker.
 */
export interface GeoUploadConversionResult {
  blobUrl: string;
  filename: string;
  fileType: string;
  sourceFormat?: string;
  sizeBytes: number;
}

/**
 * @contract CompletedGeoConversionJob
 * Purpose: A conversion/parse snapshot that has reached 'completed' with its terminal payload present.
 * Fields: All GeoConversionJobResponse fields, with `result` narrowed to non-null.
 * Rules: Only `pollGeoConversionJob` may produce this type; it rejects completed jobs with a missing payload.
 * Sync: Derived from the server-authoritative `backgroundJobs` document.
 */
export type CompletedGeoConversionJob<TResult> =
  GeoConversionJobResponse<TResult> & { status: 'completed'; result: TResult };

/** Default poll cadence — 1s keeps the wizard progress bar responsive. */
export const GEO_CONVERSION_POLL_INTERVAL_MS = 1_000;
/** Default overall deadline — 10 minutes covers multi-GB DuckDB conversions. */
export const GEO_CONVERSION_POLL_TIMEOUT_MS = 600_000;
/** Default stale threshold — 5 minutes without any job change means the worker was lost. */
export const GEO_CONVERSION_STALE_MS = 300_000;

/**
 * @contract PollGeoConversionJobOptions
 * Purpose: Injection seam and tuning knobs for the shared conversion-job polling loop.
 * Fields: jobId, fetchJob, intervalMs?, timeoutMs?, staleMs?, onProgress?, onEvent?.
 * Rules: `fetchJob` must perform the host's authenticated GET; it is the only network seam.
 * Sync: n/a — options are transient call parameters.
 */
export interface PollGeoConversionJobOptions<TResult> {
  jobId: string;
  fetchJob: (jobId: string) => Promise<GeoConversionJobResponse<TResult>>;
  intervalMs?: number;
  timeoutMs?: number;
  staleMs?: number;
  onProgress?: (job: GeoConversionJobResponse<TResult>) => void;
  onEvent?: (event: 'started' | 'completed' | 'failed', properties: Record<string, string>) => void;
}

/**
 * @reusable pollGeoConversionJob
 * Usage: Host import-run services await this after an API returns an AsyncGeoSubmission for a geo upload or geo parse.
 * Rules: Stateful workflow helper (bounded polling loop); performs no persistence and owns no host dependency.
 *
 * @process PollGeoConversionJob
 * Steps: Poll the injected job reader, emit progress, return the completed payload, reject failed/cancelled/stale/timed-out jobs.
 * Inputs: Job id, host job reader, and optional interval, timeout, stale threshold, progress callback, and event sink.
 * Outputs: The completed GeoConversionJobResponse, type-narrowed to carry a non-null result.
 * Failure Handling: Surfaces the sanitized worker error, or a bounded cancellation/stale/timeout error, to the calling wizard.
 * Idempotency: REQUIRED — the loop only reads job state and never writes.
 *
 * @errorHandling
 * Strategy: Fail on worker failure, cancellation, a completed job with no payload, frozen progress, or the overall deadline.
 * User Impact: The wizard shows a retryable conversion error instead of waiting indefinitely.
 * Logging: Delegated to the host through `onEvent`; this helper never logs file contents, blob URLs, or credentials.
 */
export async function pollGeoConversionJob<TResult>(
  options: PollGeoConversionJobOptions<TResult>,
): Promise<CompletedGeoConversionJob<TResult>> {
  const {
    jobId,
    fetchJob,
    intervalMs = GEO_CONVERSION_POLL_INTERVAL_MS,
    timeoutMs = GEO_CONVERSION_POLL_TIMEOUT_MS,
    staleMs = GEO_CONVERSION_STALE_MS,
    onProgress,
    onEvent,
  } = options;
  const deadline = Date.now() + timeoutMs;
  let lastMarker: string | undefined;
  let lastProgressAt = Date.now();
  onEvent?.('started', { jobId });

  while (Date.now() < deadline) {
    const job = await fetchJob(jobId);
    onProgress?.(job);

    if (job.status === 'completed') {
      if (!job.result) {
        onEvent?.('failed', { jobId, jobType: job.jobType, errorType: 'missing_result' });
        throw new Error('Geo conversion completed without a result. Please try again.');
      }
      onEvent?.('completed', { jobId, jobType: job.jobType });
      return job as CompletedGeoConversionJob<TResult>;
    }
    if (job.status === 'failed') {
      onEvent?.('failed', { jobId, jobType: job.jobType, errorType: 'worker_failed' });
      throw new Error(job.error ?? 'Geo conversion failed. Please try again.');
    }
    if (job.status === 'cancelled') {
      onEvent?.('failed', { jobId, jobType: job.jobType, errorType: 'cancelled' });
      throw new Error('Geo conversion was cancelled. Please try again.');
    }

    // Stale detection: the worker updates status, progress, or message on every
    // heartbeat. If none of them changes for `staleMs`, the execution was lost
    // and the job will never reach a terminal state.
    const marker = JSON.stringify([job.status, job.progress, job.message]);
    if (marker !== lastMarker) {
      lastMarker = marker;
      lastProgressAt = Date.now();
    } else if (Date.now() - lastProgressAt > staleMs) {
      onEvent?.('failed', { jobId, jobType: job.jobType, errorType: 'stale' });
      throw new Error('Geo conversion was interrupted (worker may have restarted). Please try again.');
    }

    await new Promise(resolve => setTimeout(resolve, intervalMs));
  }

  onEvent?.('failed', { jobId, errorType: 'timeout' });
  throw new Error('Geo conversion timed out after waiting too long. Please try again.');
}

/**
 * @reusable isAsyncGeoSubmission
 * Usage: Host upload/parse services branch on this to tell a synchronous non-geo response apart from a worker handoff.
 * Rules: Presentation-free pure type guard; no network, persistence, or host dependency.
 */
export function isAsyncGeoSubmission(value: unknown): value is AsyncGeoSubmission {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Record<string, unknown>;
  return typeof candidate.jobId === 'string'
    && (candidate.status === 'converting' || candidate.status === 'parsing');
}
